import { useState } from 'react';
import AnimatedStat from '@/components/base/AnimatedStat';
import { useTranslation } from 'react-i18next';

export default function TestimonialsFAQSection() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const { t } = useTranslation();

  const testimonials = [
    { quote: t('testimonials.items.0.quote'), name: t('testimonials.items.0.name'), role: t('testimonials.items.0.role'), rating: '5.0' },
    { quote: t('testimonials.items.1.quote'), name: t('testimonials.items.1.name'), role: t('testimonials.items.1.role'), rating: '5.0' },
    { quote: t('testimonials.items.2.quote'), name: t('testimonials.items.2.name'), role: t('testimonials.items.2.role'), rating: '5.0' },
    { quote: t('testimonials.items.3.quote'), name: t('testimonials.items.3.name'), role: t('testimonials.items.3.role'), rating: '4.9' },
    { quote: t('testimonials.items.4.quote'), name: t('testimonials.items.4.name'), role: t('testimonials.items.4.role'), rating: '5.0' },
    { quote: t('testimonials.items.5.quote'), name: t('testimonials.items.5.name'), role: t('testimonials.items.5.role'), rating: '4.8' },
  ];

  const faqs = [
    { q: t('faq.homeFaqs.0.q'), a: t('faq.homeFaqs.0.a') },
    { q: t('faq.homeFaqs.1.q'), a: t('faq.homeFaqs.1.a') },
    { q: t('faq.homeFaqs.2.q'), a: t('faq.homeFaqs.2.a') },
    { q: t('faq.homeFaqs.3.q'), a: t('faq.homeFaqs.3.a') },
  ];

  return (
    <>
      <section className="py-24 bg-background-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
              <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">{t('testimonials.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 max-w-3xl mx-auto leading-tight">
              {t('testimonials.titleLine1')}<br />{t('testimonials.titleLine2')}
            </h2>
          </div>
          <div className="relative mb-6">
            <div className="flex gap-6 animate-marquee">
              {[...testimonials, ...testimonials].map((tm, i) => (
                <div key={i} className="bg-white rounded-3xl p-8 border border-background-200/70 shrink-0 w-[380px]">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-primary-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <p className="text-foreground-600 leading-relaxed mb-6 text-sm">&ldquo;{tm.quote}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-700 font-bold text-sm">{tm.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="font-semibold text-foreground-950 text-sm">{tm.name}</div>
                      <div className="text-foreground-500 text-xs">{tm.role}</div>
                    </div>
                    <div className="ml-auto border border-primary-200/70 rounded-full px-2.5 py-0.5">
                      <span className="text-primary-700 text-xs font-semibold">{tm.rating}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="flex gap-6 animate-marquee-reverse">
              {[...testimonials.slice().reverse(), ...testimonials.slice().reverse()].map((tm, i) => (
                <div key={i} className="bg-white rounded-3xl p-8 border border-background-200/70 shrink-0 w-[380px]">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-primary-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <p className="text-foreground-600 leading-relaxed mb-6 text-sm">&ldquo;{tm.quote}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-700 font-bold text-sm">{tm.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="font-semibold text-foreground-950 text-sm">{tm.name}</div>
                      <div className="text-foreground-500 text-xs">{tm.role}</div>
                    </div>
                    <div className="ml-auto border border-primary-200/70 rounded-full px-2.5 py-0.5">
                      <span className="text-primary-700 text-xs font-semibold">{tm.rating}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-12 max-w-lg mx-auto">
            <div className="bg-background-950 rounded-3xl p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/20 rounded-full blur-3xl"></div>
              <div className="relative z-10 flex items-center gap-6">
                <div className="w-14 h-14 bg-primary-500 rounded-2xl flex items-center justify-center shrink-0">
                  <i className="ri-shield-star-line text-background-950 text-2xl w-7 h-7 flex items-center justify-center"></i>
                </div>
                <AnimatedStat value={4.9} suffix="/5.0" label="" decimals={1} valueClassName="text-white" />
                <div className="flex gap-0.5 mt-1">
                  {[...Array(5)].map((_, j) => (
                    <i key={j} className="ri-star-fill text-primary-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                  ))}
                </div>
                <div className="ml-auto text-right">
                  <div className="text-2xl font-bold font-heading">500+</div>
                  <div className="text-background-300 text-xs">{t('testimonials.verifiedReviews')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-24 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
                <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">{t('faq.badge')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 mb-10 leading-tight">
                {t('faq.title')}
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className={`rounded-2xl border transition-all duration-300 ${
                      openFaq === i ? 'bg-white border-primary-300/60 shadow-sm' : 'bg-white border-background-200/70'
                    }`}
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                    >
                      <span className="font-semibold text-foreground-950 text-sm pr-4">{faq.q}</span>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${openFaq === i ? 'bg-primary-500' : 'bg-background-100'}`}>
                        <i className={`${openFaq === i ? 'ri-subtract-line text-background-950' : 'ri-add-line text-foreground-600'} w-4 h-4 flex items-center justify-center`}></i>
                      </div>
                    </button>
                    {openFaq === i && (
                      <div className="px-6 pb-5 text-foreground-500 text-sm leading-relaxed">{faq.a}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="rounded-3xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Team%20of%20Black%20African%20telecommunications%20engineers%20and%20BTP%20construction%20workers%20collaborating%20at%20telecom%20infrastructure%20site%20in%20Africa%2C%20professional%20dark-skinned%20technicians%20in%20safety%20helmets%20and%20high-visibility%20vests%20reviewing%20blueprints%20together%2C%20bright%20sunny%20day%20with%20modern%20telecom%20tower%20in%20background%2C%20corporate%20team%20photography&width=800&height=600&seq=faq-team-01&orientation=landscape"
                  alt="Équipe IS TELECOM au travail"
                  className="w-full h-[550px] object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-primary-500 rounded-2xl -z-10 animate-float"></div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}